import { useState } from "react";
import { HiArrowRight } from "react-icons/hi2";
import { Calendar, Sms, Location, Global } from "iconsax-react";
import logoSrc from "@/assets/logo.svg";
import whatsappIcon from "@/assets/whatsapp.svg";
import { useTranslation } from "react-i18next";

import { useContacto } from "@/features/contacto/hooks/useContacto";
import { ContactoMapModal } from "@/features/contacto/components/ContactoMapModal";

export function ContactoPage() {
  const { t } = useTranslation("contacto");
  const { contacto, loading } = useContacto();
  const [showMap, setShowMap] = useState(false);

  const handleEmail = (email: string) => {
    window.location.href = `mailto:${email}`;
  };
  const handleWeb = (url: string) =>
    window.open(
      url.startsWith("http") ? url : `https://${url}`,
      "_blank",
      "noopener,noreferrer",
    );
  const handleWhatsapp = (tel: string) =>
    window.open(
      `https://wa.me/${tel.replace(/\D/g, "")}`,
      "_blank",
      "noopener,noreferrer",
    );

  return (
    <>
      <div className="grid h-full min-h-0 flex-1 grid-cols-1 grid-rows-[1fr] gap-[0.75rem] bg-[#f7f8fa] p-3 box-border lg:grid-cols-[1.4fr_1fr] lg:gap-5 lg:px-5 lg:py-10">
        <div className="relative min-h-[16rem] overflow-hidden rounded-xl self-stretch max-lg:max-h-[22rem] lg:min-h-[32rem]">
          {loading ? (
            <div className="h-full w-full rounded-none bg-[linear-gradient(90deg,var(--color-border-soft)_25%,#e8eff6_50%,var(--color-border-soft)_75%)] bg-size-[200%_100%] animate-[skeleton-shimmer_1.4s_ease_infinite]" />
          ) : contacto?.imageUrl ? (
            <img
              src={contacto.imageUrl}
              alt={t("officesAlt")}
              className="block h-full w-full object-cover"
            />
          ) : (
            <div className="h-full w-full bg-[linear-gradient(135deg,#0a2540_0%,#0066aa_100%)]" />
          )}
          {!loading && (
            <div className="absolute inset-0 flex items-start justify-center bg-[linear-gradient(to_bottom,rgba(0,0,0,0.55)_0%,rgba(0,0,0,0)_50%)] px-6 pt-10 max-lg:px-4 max-lg:pt-8">
              <img
                src={logoSrc}
                alt="SynerGy"
                className="block h-auto w-full max-w-84"
              />
            </div>
          )}
        </div>

        <div className="z-[1] flex flex-col gap-3 self-center lg:-ml-12">
          {loading ? (
            <div className="mx-auto h-8 w-[55%] rounded-sm bg-[linear-gradient(90deg,var(--color-border-soft)_25%,#e8eff6_50%,var(--color-border-soft)_75%)] bg-size-[200%_100%] animate-[skeleton-shimmer_1.4s_ease_infinite]" />
          ) : (
            <h1 className="m-0 mb-5 text-center text-[1.75rem] font-bold text-text-primary">
              {t("title")}
            </h1>
          )}
          <div className="flex flex-col gap-4 rounded-[0.375rem] bg-white p-0 shadow-[-6px_0_32px_rgba(0,0,0,0.15)] max-lg:rounded-md max-lg:p-6 max-lg:shadow-none">
            {loading && (
              <div className="flex flex-col">
                {[1, 2, 3, 4, 5].map((n) => (
                  <div
                    key={n}
                    className="h-[4.5rem] bg-[linear-gradient(90deg,var(--color-border-soft)_25%,#e8eff6_50%,var(--color-border-soft)_75%)] bg-size-[200%_100%] animate-[skeleton-shimmer_1.4s_ease_infinite]"
                  />
                ))}
              </div>
            )}

            {contacto && !loading && (
              <ul className="m-0 flex list-none flex-col p-0" role="list">
                {contacto.horarioAtencion && (
                  <li className="flex items-center gap-4 border-b border-[#aac4db] px-3 py-4">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-transparent text-brand-primary">
                      <Calendar
                        size={22}
                        variant="Linear"
                        color="currentColor"
                      />
                    </div>
                    <div className="flex flex-1 flex-col gap-0.5">
                      <span className="text-[0.9375rem] font-semibold text-text-primary">
                        {t("horario")}
                      </span>
                      <span className="text-[0.8125rem] text-text-muted">
                        {contacto.horarioAtencion}
                      </span>
                    </div>
                  </li>
                )}

                {contacto.telefono && (
                  <li
                    className="flex cursor-pointer items-center gap-4 border-b border-[#aac4db] px-3 py-4 transition-colors duration-150 last:rounded-b-[0.375rem] hover:bg-[#eef0f4]"
                    role="button"
                    tabIndex={0}
                    onClick={() => handleWhatsapp(contacto.telefono)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && handleWhatsapp(contacto.telefono)
                    }
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-transparent text-brand-primary">
                      <img
                        src={whatsappIcon}
                        alt="WhatsApp"
                        width={22}
                        height={22}
                      />
                    </div>
                    <div className="flex flex-1 flex-col gap-0.5">
                      <span className="text-[0.9375rem] font-semibold text-text-primary">
                        {t("whatsapp")}
                      </span>
                      <span className="text-[0.8125rem] text-text-muted">
                        {contacto.telefono}
                      </span>
                    </div>
                    <HiArrowRight className="h-5 w-5 shrink-0 text-brand-primary transition-transform duration-200" />
                  </li>
                )}

                {contacto.email && (
                  <li
                    className="flex cursor-pointer items-center gap-4 border-b border-[#aac4db] px-3 py-4 transition-colors duration-150 last:rounded-b-[0.375rem] hover:bg-[#eef0f4]"
                    role="button"
                    tabIndex={0}
                    onClick={() => handleEmail("info@synergy-apx.com")}
                    onKeyDown={(e) =>
                      e.key === "Enter" && handleEmail("info@synergy-apx.com")
                    }
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-transparent text-brand-primary">
                      <Sms size={22} variant="Linear" color="currentColor" />
                    </div>
                    <div className="flex flex-1 flex-col gap-0.5">
                      <span className="text-[0.9375rem] font-semibold text-text-primary">
                        {t("email")}
                      </span>
                      <span className="text-[0.8125rem] text-text-muted">
                        {contacto.email}
                      </span>
                    </div>
                    <HiArrowRight className="h-5 w-5 shrink-0 text-brand-primary transition-transform duration-200" />
                  </li>
                )}

                {contacto.ubicaciones.length > 0 && (
                  <li
                    className="flex cursor-pointer items-center gap-4 border-b border-[#aac4db] px-3 py-4 transition-colors duration-150 last:rounded-b-[0.375rem] hover:bg-[#eef0f4]"
                    role="button"
                    tabIndex={0}
                    onClick={() => setShowMap(true)}
                    onKeyDown={(e) => e.key === "Enter" && setShowMap(true)}
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-transparent text-brand-primary">
                      <Location
                        size={22}
                        variant="Linear"
                        color="currentColor"
                      />
                    </div>
                    <div className="flex flex-1 flex-col gap-0.5">
                      <span className="text-[0.9375rem] font-semibold text-text-primary">
                        {t("ubicacion")}
                      </span>
                      <span className="text-[0.8125rem] text-text-muted">
                        {t("findUs")}
                      </span>
                    </div>
                    <HiArrowRight className="h-5 w-5 shrink-0 text-brand-primary transition-transform duration-200" />
                  </li>
                )}

                {contacto.sitioWeb && (
                  <li
                    className="flex cursor-pointer items-center gap-4 border-b border-[#aac4db] px-3 py-4 transition-colors duration-150 last:rounded-b-[0.375rem] hover:bg-[#eef0f4]"
                    role="button"
                    tabIndex={0}
                    onClick={() => handleWeb(contacto.sitioWeb)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && handleWeb(contacto.sitioWeb)
                    }
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-transparent text-brand-primary">
                      <Global size={22} variant="Linear" color="currentColor" />
                    </div>
                    <div className="flex flex-1 flex-col gap-0.5">
                      <span className="text-[0.9375rem] font-semibold text-text-primary">
                        {t("website")}
                      </span>
                      <span className="text-[0.8125rem] text-text-muted">
                        {contacto.sitioWeb}
                      </span>
                    </div>
                    <HiArrowRight className="h-5 w-5 shrink-0 text-brand-primary transition-transform duration-200" />
                  </li>
                )}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* ── Modal mapa de ubicaciones ── */}
      {showMap && contacto && (
        <ContactoMapModal
          ubicaciones={contacto.ubicaciones}
          onClose={() => setShowMap(false)}
        />
      )}
    </>
  );
}
